import { REVENUE_DEFINITIONS } from './revenue-definitions.js';
import type { RevenueMetricKey, RevenueRecord } from '../types/revenue.js';
import type { Granularity } from './kpi-aggregation.js';

export type { Granularity } from './kpi-aggregation.js';
export { GRANULARITY_OPTIONS } from './kpi-aggregation.js';

/** @graph-connects none */
export interface AggregatedBucket {
  label: string;
  record: RevenueRecord;
}

/** 売上6カテゴリ（積み上げチャート・純売上計算の対象）。badDebt/refund は控除項目として別枠。 @graph-connects none */
export const GROSS_REVENUE_KEYS: readonly RevenueMetricKey[] = [
  'subscriptionFeeMonthly',
  'subscriptionFeeBimonthly',
  'ticketPurchaseRevenue',
  'penaltyFee',
  'itemPurchase',
  'expiredRevenue',
];

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上6カテゴリの合計（総売上）を算出する。validate.ts の純売上検算ルールと同じ6カテゴリ。
 * @graph-connects none
 */
export function grossRevenue(row: RevenueRecord): number {
  return GROSS_REVENUE_KEYS.reduce((sum, key) => sum + row[key], 0);
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 総売上から貸倒・返金を控除した純売上を算出する。
 * @graph-connects none
 */
export function netRevenue(row: RevenueRecord): number {
  return grossRevenue(row) - row.badDebt + row.badDebtRecovery - row.refund;
}

/** @graph-connects none */
export function formatYen(value: number): string {
  const abs = Math.abs(value).toLocaleString('ja-JP');
  return value < 0 ? `-¥${abs}` : `¥${abs}`;
}

/**
 * 割合（0〜1）を小数1桁のパーセントにする（0.8776 → '87.8%'）。予実のグラフの割合の目盛りとオファーの内訳の割合が使う。
 * @graph-connects none
 */
export function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

/** @graph-connects none */
export function monthKey(date: string): string {
  return date.slice(0, 7);
}

export interface MonthlyBucket {
  month: string;
  record: RevenueRecord;
}

/**
 * 日次行を月次に集約する。フロー指標（type=flow）は月内合計、ストック指標（type=stock、前受金残高）は月末値を採用する。
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 日次売上行を月次に集約する。フロー指標は月内合計、ストック指標（前受金残高）は月末値を採用する。
 * @graph-connects REVENUE_DEFINITIONS [references, via:key] 指標の flow/stock 分類を参照して集約方式を決める
 */
export function aggregateMonthly(rows: RevenueRecord[]): MonthlyBucket[] {
  const sorted = [...rows].sort((a, b) => (a.date < b.date ? -1 : 1));
  const buckets = new Map<string, RevenueRecord[]>();
  for (const row of sorted) {
    const key = monthKey(row.date);
    const arr = buckets.get(key);
    if (arr) {
      arr.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }

  const metricKeys = Object.keys(REVENUE_DEFINITIONS) as RevenueMetricKey[];
  const result: MonthlyBucket[] = [];
  for (const [month, group] of buckets) {
    const last = group[group.length - 1];
    const aggregated = { date: last.date } as RevenueRecord;
    for (const key of metricKeys) {
      aggregated[key] =
        REVENUE_DEFINITIONS[key].type === 'stock'
          ? last[key]
          : group.reduce((sum, r) => sum + r[key], 0);
    }
    result.push({ month, record: aggregated });
  }
  return result;
}

/** @graph-connects none */
export function weekKey(date: string): string {
  const d = new Date(date + 'T00:00:00Z');
  const day = d.getUTCDay();
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() - ((day + 6) % 7));
  const m = monday.getUTCMonth() + 1;
  const dd = monday.getUTCDate();
  return `${String(monday.getUTCFullYear())}-${String(m).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}

/** @graph-connects none */
export function quarterKey(date: string): string {
  const month = parseInt(date.slice(5, 7), 10);
  const quarter = Math.ceil(month / 3);
  return `${date.slice(0, 4)} Q${String(quarter)}`;
}

/** @graph-connects none */
export function yearKey(date: string): string {
  return date.slice(0, 4);
}

/** @graph-connects none */
function aggregateGeneric(
  rows: RevenueRecord[],
  keyFn: (date: string) => string,
  labelFn: (key: string) => string,
): AggregatedBucket[] {
  const sorted = [...rows].sort((a, b) => (a.date < b.date ? -1 : 1));
  const buckets = new Map<string, RevenueRecord[]>();
  for (const row of sorted) {
    const k = keyFn(row.date);
    const arr = buckets.get(k);
    if (arr) {
      arr.push(row);
    } else {
      buckets.set(k, [row]);
    }
  }

  const metricKeys = Object.keys(REVENUE_DEFINITIONS) as RevenueMetricKey[];
  const result: AggregatedBucket[] = [];
  for (const [key, group] of buckets) {
    const last = group[group.length - 1];
    const aggregated = { date: last.date } as RevenueRecord;
    for (const mk of metricKeys) {
      aggregated[mk] =
        REVENUE_DEFINITIONS[mk].type === 'stock'
          ? last[mk]
          : group.reduce((sum, r) => sum + r[mk], 0);
    }
    result.push({ label: labelFn(key), record: aggregated });
  }
  return result;
}

/** @graph-connects none */
export function weekLabel(mondayDate: string): string {
  const m = parseInt(mondayDate.slice(5, 7), 10);
  const d = parseInt(mondayDate.slice(8, 10), 10);
  return `${String(m)}/${String(d)}~`;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 日次売上行を指定粒度（日次/週次/月次/四半期/年次）に集約する。
 * @graph-connects REVENUE_DEFINITIONS [references, via:key] 指標の flow/stock 分類を参照
 */
export function aggregateByGranularity(
  rows: RevenueRecord[],
  granularity: Granularity,
): AggregatedBucket[] {
  if (granularity === 'daily') {
    return [...rows]
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map((r) => ({ label: r.date, record: r }));
  }
  if (granularity === 'weekly') {
    return aggregateGeneric(rows, weekKey, weekLabel);
  }
  if (granularity === 'monthly') {
    return aggregateGeneric(rows, monthKey, (k) => k);
  }
  if (granularity === 'quarterly') {
    return aggregateGeneric(rows, quarterKey, (k) => k);
  }
  return aggregateGeneric(rows, yearKey, (k) => k);
}

/** @graph-connects none */
export function formatSnapshotJST(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
